<script setup lang="ts">
import { computed, ref } from 'vue';
import { Icon } from '@iconify/vue';
import TutorialViewerDialog from '@/shared/components/TutorialViewerDialog.vue';
import {
  getGettingStartedUrl,
  getUserGuideUrl,
  getHelpTutorials,
} from '@/shared/utils/tutorialUrls';
import { getCurrentJurisdiction } from '@/features/startTask/preset_task/jurisdictionResolver';

const menuOpen = ref(false);
// 메뉴 내부 뷰: 'main' | 'tutorials' (드릴인)
const view = ref<'main' | 'tutorials'>('main');

// 사용중인 국가 버전 (MY/SG) — 못 알아내면 MY
const jurisdiction = computed(() => getCurrentJurisdiction() || 'MY');
const tutorials = computed(() => getHelpTutorials(jurisdiction.value));

// 전체화면 뷰어
const viewerOpen = ref(false);
const viewerUrl = ref('');
const viewerTitle = ref('');

function openViewer(url: string, title: string) {
  viewerUrl.value = url;
  viewerTitle.value = title;
  viewerOpen.value = true;
  menuOpen.value = false;
  view.value = 'main';
}

function openGettingStarted() {
  openViewer(getGettingStartedUrl(jurisdiction.value), 'Getting started');
}

function openGuide() {
  openViewer(getUserGuideUrl(jurisdiction.value), 'User Guide');
}

// 연락처
const whatsappUrl = computed(() => {
  // wa.me는 "숫자만" 요구 — + 가 남으면 링크 미리보기/로고가 안 뜸.
  const num = (import.meta.env.VITE_WHATSAPP_NUMBER || '+6587752369').replace(/\D/g, '');
  return `https://wa.me/${num}`;
});
const emailHref = 'mailto:hi@lettaga.com';

// 메뉴가 닫힐 때 항상 메인 뷰로 리셋
function onMenuUpdate(value: boolean) {
  menuOpen.value = value;
  if (!value) view.value = 'main';
}
</script>

<template>
  <v-menu
    :model-value="menuOpen"
    @update:model-value="onMenuUpdate"
    :close-on-content-click="false"
    location="bottom end"
    offset="8"
  >
    <template #activator="{ props }">
      <v-btn
        icon
        variant="text"
        size="small"
        color="primary"
        class="custom-hover-primary"
        aria-label="Help & learning"
        v-bind="props"
      >
        <Icon icon="mdi:help-circle-outline" height="22" />
      </v-btn>
    </template>

    <v-sheet rounded="lg" elevation="10" width="340" class="help-menu-sheet">
      <!-- ===== Header ===== -->
      <div class="d-flex align-center px-4 pt-4 pb-2">
        <v-btn
          v-if="view === 'tutorials'"
          icon
          variant="text"
          size="x-small"
          class="me-1"
          aria-label="Back"
          @click="view = 'main'"
        >
          <Icon icon="mdi:chevron-left" height="22" />
        </v-btn>
        <div>
          <h6 class="text-subtitle-1 font-weight-bold">
            {{ view === 'tutorials' ? 'Tutorials' : 'Help & learning' }}
          </h6>
          <p class="text-caption textSecondary mb-0">
            {{ view === 'tutorials' ? 'Pick a screen to follow' : 'Tours, docs and how to reach us' }}
          </p>
        </div>
        <v-spacer />
        <v-chip v-if="view === 'tutorials'" size="x-small" color="primary" variant="tonal">
          {{ jurisdiction }}
        </v-chip>
      </div>

      <v-divider />

      <!-- ===== Main view ===== -->
      <v-list v-if="view === 'main'" density="comfortable" class="py-1">
        <v-list-item @click="openGettingStarted">
          <template #prepend>
            <v-avatar size="36" rounded="lg" color="lightprimary">
              <Icon icon="mdi:rocket-launch-outline" height="20" class="text-primary" />
            </v-avatar>
          </template>
          <v-list-item-title class="text-subtitle-2 font-weight-bold">Getting started</v-list-item-title>
          <v-list-item-subtitle class="text-caption">Resume your setup</v-list-item-subtitle>
          <template #append><Icon icon="mdi:chevron-right" height="18" class="textSecondary" /></template>
        </v-list-item>

        <v-list-item @click="openGuide">
          <template #prepend>
            <v-avatar size="36" rounded="lg" color="lightwarning">
              <Icon icon="mdi:book-open-variant" height="20" class="text-warning" />
            </v-avatar>
          </template>
          <v-list-item-title class="text-subtitle-2 font-weight-bold">User Guide</v-list-item-title>
          <v-list-item-subtitle class="text-caption">Full reference, by task</v-list-item-subtitle>
          <template #append><Icon icon="mdi:chevron-right" height="18" class="textSecondary" /></template>
        </v-list-item>

        <v-list-item @click="view = 'tutorials'">
          <template #prepend>
            <v-avatar size="36" rounded="lg" color="lightsuccess">
              <Icon icon="mdi:play-circle-outline" height="20" class="text-success" />
            </v-avatar>
          </template>
          <v-list-item-title class="text-subtitle-2 font-weight-bold">Tutorials</v-list-item-title>
          <v-list-item-subtitle class="text-caption">Pick a screen to follow</v-list-item-subtitle>
          <template #append><Icon icon="mdi:chevron-right" height="18" class="textSecondary" /></template>
        </v-list-item>

        <v-divider class="my-1" />
        <div class="px-4 py-1 text-overline textSecondary">Contact us</div>

        <v-list-item :href="whatsappUrl" target="_blank" rel="noopener">
          <template #prepend>
            <v-avatar size="36" rounded="lg" style="background: #e6f8ef">
              <v-icon size="20" color="#25D366">mdi-whatsapp</v-icon>
            </v-avatar>
          </template>
          <v-list-item-title class="text-subtitle-2 font-weight-bold">WhatsApp</v-list-item-title>
          <v-list-item-subtitle class="text-caption">Quick chat · migration help</v-list-item-subtitle>
          <template #append><Icon icon="mdi:open-in-new" height="16" class="textSecondary" /></template>
        </v-list-item>

        <v-list-item :href="emailHref">
          <template #prepend>
            <v-avatar size="36" rounded="lg" color="lightprimary">
              <Icon icon="mdi:email-outline" height="20" class="text-primary" />
            </v-avatar>
          </template>
          <v-list-item-title class="text-subtitle-2 font-weight-bold">Email us</v-list-item-title>
          <v-list-item-subtitle class="text-caption">hi@lettaga.com</v-list-item-subtitle>
        </v-list-item>
      </v-list>

      <!-- ===== Tutorials drill-in view ===== -->
      <div v-else>
        <perfect-scrollbar style="max-height: 380px">
          <v-list density="comfortable" class="py-1">
            <v-list-item
              v-for="item in tutorials"
              :key="item.key"
              @click="openViewer(item.url, item.title)"
            >
              <template #prepend>
                <v-avatar size="32" rounded="lg" color="lightsuccess">
                  <v-icon size="18" color="success">{{ item.icon }}</v-icon>
                </v-avatar>
              </template>
              <v-list-item-title class="text-subtitle-2 font-weight-bold">{{ item.title }}</v-list-item-title>
              <v-list-item-subtitle class="text-caption">{{ item.subtitle }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </perfect-scrollbar>
        <v-divider />
        <p class="text-caption textSecondary px-4 py-3 mb-0">
          Each screen also has its own <b>View Tutorial</b> for in-context help.
        </p>
      </div>
    </v-sheet>
  </v-menu>

  <TutorialViewerDialog v-model="viewerOpen" :url="viewerUrl" :title="viewerTitle" />
</template>

<style scoped>
.help-menu-sheet {
  overflow: hidden;
}
</style>
